import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Trophy, Flame, Crown, Swords, Gift, Video, BadgeCheck, Sparkles } from "lucide-react";

const TYPES = [
  { id: "streamers", label: "Streamers", icon: Video, color: "#FF2A5F" },
  { id: "gifters", label: "Gifters", icon: Gift, color: "#FFD700" },
  { id: "gifter_battle", label: "Gifter Battle", icon: Swords, color: "#00E5FF" },
  { id: "creator_battle", label: "Creator Battle", icon: Crown, color: "#A855F7" },
  { id: "video_gifter", label: "Video Gifter", icon: Sparkles, color: "#10B981" },
];
const PERIODS = [
  { id: "today", label: "Today" },
  { id: "yesterday", label: "Yesterday" },
  { id: "7d", label: "7 Days" },
  { id: "30d", label: "30 Days" },
  { id: "90d", label: "90 Days" },
];

const BADGE_MAP = {
  rookie: { emoji: "🎯", color: "#94A3B8" },
  bronze: { emoji: "🥉", color: "#CD7F32" },
  silver: { emoji: "🥈", color: "#C0C0C0" },
  gold: { emoji: "🥇", color: "#FFD700" },
  diamond: { emoji: "💎", color: "#00E5FF" },
  legend: { emoji: "👑", color: "#FF2A5F" },
};

function RankRow({ item }) {
  const b = BADGE_MAP[item.user?.stream_badge] || BADGE_MAP.rookie;
  const medal = item.rank === 1 ? "🥇" : item.rank === 2 ? "🥈" : item.rank === 3 ? "🥉" : `#${item.rank}`;
  return (
    <Link to={`/u/${item.user?.id}`} data-testid={`lb-row-${item.rank}`}
      className="flex items-center gap-3 p-3 rounded-2xl bg-white/5 border border-white/10 active:scale-[0.99] transition">
      <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm ${item.rank<=3?"bg-gradient-to-br from-[#FF7A00] to-[#FF2A5F] text-black":"bg-white/10"}`}>
        {item.rank <= 3 ? <span className="text-xl">{medal}</span> : <span className="text-white/70">{medal}</span>}
      </div>
      <img src={item.user?.avatar} alt="" className="w-10 h-10 rounded-full"/>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1">
          <span className="font-bold truncate">@{item.user?.username || "user"}</span>
          {item.user?.verified && <BadgeCheck className="w-3.5 h-3.5 text-[#00E5FF] flex-shrink-0"/>}
          <span className="text-[10px]" style={{color: b.color}}>{b.emoji}</span>
        </div>
        <div className="text-[10px] text-white/60">{item.label}</div>
      </div>
      <div className="text-right">
        <div className="font-outfit font-bold text-[#FF7A00]">{item.secondary || item.score}</div>
      </div>
    </Link>
  );
}

function BattleCard({ battle }) {
  if (!battle) return null;
  const { left, right, leader_user_id, diff } = battle;
  const total = left.score + right.score || 1;
  const leftPct = (left.score / total) * 100;
  const leftLeading = leader_user_id === left.user.id;
  return (
    <div className="rounded-3xl p-4 bg-gradient-to-br from-[#FF2A5F]/15 to-[#00E5FF]/15 border border-white/10 mb-4" data-testid="battle-card">
      <div className="flex items-center justify-center gap-2 mb-3">
        <Swords className="w-5 h-5 text-[#FF7A00]"/>
        <h3 className="font-outfit font-extrabold text-lg">Head-to-Head Battle</h3>
      </div>
      <div className="flex items-center justify-between gap-2 mb-2">
        <Link to={`/u/${left.user.id}`} className={`flex-1 flex items-center gap-2 p-2 rounded-2xl ${leftLeading?"bg-[#FF7A00]/15 border border-[#FF7A00]/40":"bg-white/5 border border-white/10"}`}>
          <img src={left.user.avatar} className="w-10 h-10 rounded-full"/>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold truncate">@{left.user.username}</div>
            <div className="text-[10px] text-[#FF7A00]">{left.score.toLocaleString()}</div>
          </div>
          {leftLeading && <Crown className="w-4 h-4 text-[#FF7A00]"/>}
        </Link>
        <div className="font-outfit font-extrabold text-2xl text-[#FF2A5F]">VS</div>
        <Link to={`/u/${right.user.id}`} className={`flex-1 flex items-center gap-2 p-2 rounded-2xl ${!leftLeading?"bg-[#FF7A00]/15 border border-[#FF7A00]/40":"bg-white/5 border border-white/10"}`}>
          {!leftLeading && <Crown className="w-4 h-4 text-[#FF7A00]"/>}
          <div className="flex-1 min-w-0 text-right">
            <div className="text-xs font-bold truncate">@{right.user.username}</div>
            <div className="text-[10px] text-[#FF7A00]">{right.score.toLocaleString()}</div>
          </div>
          <img src={right.user.avatar} className="w-10 h-10 rounded-full"/>
        </Link>
      </div>
      <div className="h-2 bg-white/10 rounded-full overflow-hidden flex">
        <div className="h-full bg-[#00E5FF]" style={{width: `${leftPct}%`}}/>
        <div className="h-full bg-[#FF2A5F]" style={{width: `${100 - leftPct}%`}}/>
      </div>
      <div className="text-center text-[10px] text-white/60 mt-1.5">
        Lead by <b className="text-[#FF7A00]">{diff.toLocaleString()}</b>
      </div>
    </div>
  );
}

export default function Leaderboard() {
  const [type, setType] = useState("streamers");
  const [period, setPeriod] = useState("7d");
  const [data, setData] = useState({ items: [], battle: null });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/leaderboard?type=${type}&period=${period}`).then(r => { setData(r.data); setLoading(false); }).catch(()=>setLoading(false));
  }, [type, period]);

  return (
    <div className="px-4 py-6">
      <div className="flex items-center gap-2 mb-1">
        <Trophy className="w-7 h-7 text-[#FF7A00]"/>
        <h1 className="font-outfit font-extrabold text-3xl tracking-tighter">Leaderboard</h1>
      </div>
      <p className="text-white/60 text-sm mb-4">Top performers across the Hifireels universe</p>

      <div className="flex gap-1.5 overflow-x-auto scrollbar-hide mb-3 -mx-4 px-4">
        {TYPES.map(t => (
          <button key={t.id} onClick={()=>setType(t.id)} data-testid={`type-${t.id}`}
            className={`flex-shrink-0 px-3 py-2 rounded-full text-xs font-bold border ${type===t.id?"bg-white/10":"bg-white/5"}`}
            style={{borderColor: type===t.id ? t.color : "rgba(255,255,255,0.1)", color: type===t.id ? t.color : "#fff"}}>
            <t.icon className="inline w-3.5 h-3.5 mr-1"/>{t.label}
          </button>
        ))}
      </div>

      <div className="flex gap-1.5 overflow-x-auto scrollbar-hide mb-4 -mx-4 px-4">
        {PERIODS.map(p => (
          <button key={p.id} onClick={()=>setPeriod(p.id)} data-testid={`period-${p.id}`}
            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border ${period===p.id?"border-[#00E5FF] bg-[#00E5FF]/10 text-[#00E5FF]":"border-white/10 bg-white/5 text-white/70"}`}>
            {p.label}
          </button>
        ))}
      </div>

      {(type === "gifter_battle" || type === "creator_battle") && data.battle && <BattleCard battle={data.battle}/>}

      {loading ? (
        <div className="text-center py-12 text-white/50">Loading...</div>
      ) : data.items.length === 0 ? (
        <div className="text-center py-12 text-white/50">
          <Flame className="w-10 h-10 mx-auto mb-2 opacity-50"/>
          <p>No data for this period yet</p>
          <p className="text-xs mt-1">Be the first to rank!</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="lb-list">
          {data.items.map(it => <RankRow key={`${it.rank}-${it.user?.id}`} item={it}/>)}
        </div>
      )}
    </div>
  );
}
